// @erp/ui/side-panel — the app's editing/viewing surface.
//
// THE RULE (product decision, 2026-08-22): a MODAL is only ever a confirmation
// ("are you sure?"). Everything that holds a form, a record, or a document —
// create/edit forms, the library card, a proof viewer, an audit trail — opens
// in THIS right-anchored panel instead.
//
// Two properties a modal cannot give us, and the reason this component exists:
//
//   1. URL ADDRESSABILITY. The panel's open/target/expanded state lives in the
//      route's search params (see usePanelState below), so Back closes it,
//      Forward re-opens it, and a pasted link opens straight into the record.
//      The list underneath stays MOUNTED — its cursor, filters and scroll
//      survive, which is exactly what a full detail route would destroy.
//   2. EXPANSION. Dense records (a MARC form, a wide table) need room; the
//      header's expand toggle widens the panel and that choice is in the URL
//      too, so it survives a reload and travels with a shared link.
//
// Built on daisyUI's native `<dialog class="modal modal-bottom sm:modal-end">` (canonical
// placement modifier — Blueprint syntax) so focus trapping, Esc-to-cancel and
// the backdrop come from the platform rather than hand-rolled key handlers.
// The Modal component's controlled-open effect is preserved verbatim, including
// its jsdom fallback, so panels are testable in the existing harness.
import { Show, createEffect, createUniqueId, type Component, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Maximize2, Minimize2, X } from "lucide-solid";
import { IconButton } from "../icon-button/index.js";

export interface SidePanelProps {
  /**
   * Controlled visibility — normally `usePanelState().open()`, i.e. the URL.
   * The panel never toggles itself: Esc, the backdrop and ✕ all call `onClose`
   * and wait for the URL to change, so Back/Forward stay truthful.
   */
  open: boolean;
  /** Clear the panel's search params (`usePanelState().close`). Fired by ✕, Esc and the backdrop. */
  onClose: () => void;
  /** The panel's accessible name (`aria-labelledby`): the record's name or the verb — "Edit patron", "Library card". */
  title: string;
  /** One line under the title — the record's identifier or state, not a repeat of the title. */
  subtitle?: string;
  /** A lucide-solid COMPONENT (`icon={UserRound}`) in a primary-tint capsule beside the title; the panel sizes it. */
  icon?: Component<{ size?: number }>;
  /** Expanded state — controlled by the caller so it can live in the URL. */
  wide?: boolean;
  /** Omit to hide the expand affordance (a narrow panel that never widens). */
  onToggleWide?: () => void;
  /** Sticky action row (Cancel / Save …). */
  footer?: JSX.Element;
  /** Automation id on the `<dialog>`; also seeds the title id (`${id}-title`). */
  id?: string;
  /** The body — a form, a record, a viewer. Scrolls inside the panel; header and footer stay put. */
  children: JSX.Element;
}

/* Complete static class strings per width state — never assembled inline.
 * Mobile-first: under `sm` the box is a bottom SHEET (full width, capped at
 * 85dvh, rounded top, square bottom, top border); from `sm` up it is exactly
 * the right-anchored panel it always was.
 * The width change takes 250ms = --spectrum-animation-duration-500, Spectrum's
 * dialog ENTRY rung (spectrum-css modal/index.css:90-104): the panel is
 * arriving at a new size, not flipping state in place, so the 130ms substrate
 * default would be wrong for it. (Spectrum's full asymmetric open/close
 * choreography is deliberately unported — see spectrum.css §6.) */
const BOX_NARROW =
  "modal-box flex w-full flex-col overflow-hidden border-base-300 bg-base-100 p-0 transition-[max-width] duration-[250ms] max-sm:max-h-[85dvh] max-sm:rounded-t-box max-sm:rounded-b-none max-sm:border-t sm:h-full sm:max-h-none sm:max-w-xl sm:rounded-none sm:border-l";
const BOX_WIDE =
  "modal-box flex w-full flex-col overflow-hidden border-base-300 bg-base-100 p-0 transition-[max-width] duration-[250ms] max-sm:max-h-[85dvh] max-sm:rounded-t-box max-sm:rounded-b-none max-sm:border-t sm:h-full sm:max-h-none sm:max-w-5xl sm:rounded-none sm:border-l";

/**
 * The app's editing and viewing surface: a right-anchored panel (a bottom
 * sheet on phones) whose open/target/expanded state lives in the URL, so Back
 * closes it and a pasted link opens straight into the record while the list
 * underneath stays mounted. Pair it with `usePanelState` for the wiring.
 */
export function SidePanel(props: SidePanelProps) {
  const uid = createUniqueId();
  const titleId = () => `${props.id ?? uid}-title`;
  let dialog: HTMLDialogElement | undefined;

  createEffect(() => {
    const el = dialog;
    if (!el) return;
    if (props.open && !el.open) {
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", ""); // jsdom / older engines fallback
    } else if (!props.open && el.open) {
      if (typeof el.close === "function") el.close();
      else el.removeAttribute("open");
    }
  });

  // Native close (Esc cancel, backdrop submit) → tell the controller, which
  // clears the URL state; the panel never closes itself behind the URL's back.
  const handleClose = () => {
    if (props.open) props.onClose();
  };

  return (
    <dialog
      ref={dialog}
      id={props.id}
      class="modal modal-bottom sm:modal-end"
      aria-labelledby={titleId()}
      onClose={handleClose}
    >
      <div class={props.wide ? BOX_WIDE : BOX_NARROW}>
        <div class="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-base-300 sm:hidden" aria-hidden="true" />
        <div class="flex shrink-0 items-center gap-3 border-b border-base-300 px-5 py-3.5">
          <Show when={props.icon}>
            <div class="flex size-8 shrink-0 items-center justify-center rounded-field bg-primary-tint text-primary">
              <Dynamic component={props.icon!} size={16} />
            </div>
          </Show>
          <div class="min-w-0 flex-1">
            <h3 id={titleId()} class="text-sm leading-tight">
              {props.title}
            </h3>
            <Show when={props.subtitle}>
              <p class="mt-0.5 text-xs text-base-subtle">{props.subtitle}</p>
            </Show>
          </div>
          <Show when={props.onToggleWide}>
            {/* Both header controls are @erp/ui/icon-button: the 32px hit
                floor (40 at touch) around Spectrum's 18px header glyph, a
                rounded square. The expand toggle is a STATE switch, so it
                keeps its tooltip — the label names what the press produces —
                opening downward, into the box, where nothing clips it. The
                sheet form under `sm` never widens, so the toggle hides there. */}
            <IconButton
              icon={props.wide ? Minimize2 : Maximize2}
              label={props.wide ? "Collapse panel" : "Expand panel"}
              size="m"
              placement="bottom"
              pressed={!!props.wide}
              class="max-sm:hidden"
              onClick={() => props.onToggleWide?.()}
            />
          </Show>
          {/* No tooltip on the close X — Adobe's CloseButton has none. */}
          <IconButton icon={X} label="Close panel" size="m" tooltip={false} onClick={() => props.onClose()} />
        </div>

        <div class="flex-1 overflow-y-auto p-5">{props.children}</div>

        <Show when={props.footer}>
          <div class="modal-action mt-0 flex shrink-0 items-center justify-end gap-2 border-t border-base-300 bg-base-200 px-5 py-3.5 max-sm:pb-[calc(0.875rem+env(safe-area-inset-bottom))]">
            {props.footer}
          </div>
        </Show>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button aria-label="Close panel">close</button>
      </form>
    </dialog>
  );
}

/* ───────────────────────── the URL half of the contract ─────────────────────
 * A panel is a VIEW, and views belong in the URL. These two helpers keep every
 * screen's wiring identical: `panelSearch` heals the params in the route's
 * validateSearch, `usePanelState` reads/writes them in the screen.
 */

/** The healed panel params. A default value is ABSENT from the URL (AR/finance idiom). */
export interface PanelSearch {
  panel?: string;
  panelId?: string;
  panelWide?: "1";
}

/**
 * Self-healing validator for a route's panel params. Pass the closed set of
 * panel names that route may open; anything else heals to "no panel". `panelId`
 * and `panelWide` only survive while a panel is actually open, so a stale id
 * can never linger in a shared link.
 *
 * Spread it into the route's existing validateSearch:
 *   validateSearch: (i) => ({ ...validateBooksSearch(i), ...panelSearch(["new","edit"], i) })
 */
export function panelSearch(names: readonly string[], input: Record<string, unknown>): PanelSearch {
  const raw = input.panel;
  const panel = typeof raw === "string" && names.includes(raw) ? raw : undefined;
  if (!panel) return {};
  const id = input.panelId;
  return {
    panel,
    panelId: typeof id === "string" && id.length > 0 ? id : undefined,
    // Coerced from the primitive (UX-17): the house codec hands "1" through as text; a foreign one's number 1 is the same flag.
    panelWide: String(input.panelWide) === "1" ? "1" : undefined,
  };
}

export interface PanelState<N extends string> {
  /** The open panel's name, or undefined when none is open. */
  panel: () => N | undefined;
  /** The record the panel targets — absent means "create". */
  panelId: () => string | undefined;
  /** Expanded state, straight from the URL. */
  wide: () => boolean;
  /** `panel() === name` — the SidePanel `open` prop. */
  isOpen: (name: N) => boolean;
  /** Push a history entry so Back closes the panel. */
  open: (name: N, id?: string) => void;
  close: () => void;
  /** REPLACES history — expanding must not make Back un-expand instead of closing. */
  toggleWide: () => void;
}

/**
 * Read/write the panel params on the CURRENT route. `strict: false` keeps this
 * usable from any screen without threading the route's generated types.
 */
export function usePanelState<N extends string = string>(): PanelState<N> {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as unknown as () => Record<string, unknown>;

  const panel = () => (typeof search().panel === "string" ? (search().panel as N) : undefined);
  const panelId = () => (typeof search().panelId === "string" ? (search().panelId as string) : undefined);
  const wide = () => search().panelWide === "1";

  const go = (next: Record<string, unknown>, replace: boolean): void => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
      replace,
    } as never);
  };

  return {
    panel,
    panelId,
    wide,
    isOpen: (name) => panel() === name,
    open: (name, id) => go({ panel: name, panelId: id, panelWide: undefined }, false),
    close: () => go({ panel: undefined, panelId: undefined, panelWide: undefined }, false),
    toggleWide: () => go({ panelWide: wide() ? undefined : "1" }, true),
  };
}
